<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { cloudApi } from '@/api/index.js'

const props = defineProps({ tenantId: String, projectId: String })
const emit = defineEmits(['changed'])
const data = ref(null), error = ref(''), busy = ref(false), choices = ref({}), message = ref('')
let generation = 0
const path = () => `/api/tenants/${encodeURIComponent(props.tenantId)}/projects/${encodeURIComponent(props.projectId)}`
const pending = computed(() => (data.value?.items || []).filter(item => !item.eligible))
async function load() {
  const token = ++generation
  busy.value = true; error.value = ''
  try {
    const result = await cloudApi.get(`${path()}/dispatch-binding-readiness`)
    if (token !== generation) return
    if (!Number.isInteger(result?.production_count) || !Array.isArray(result?.items)) throw Error('接收名单尚未完整读取，请重新读取。')
    data.value = result
    for (const item of result.items) if (!choices.value[item.app_binding_id] && item.suggested_persona_id) choices.value[item.app_binding_id] = item.suggested_persona_id
  } catch (cause) { if (token === generation) error.value = cause.message || '接收名单暂时无法读取。' }
  finally { if (token === generation) busy.value = false }
}
async function confirm(item) {
  if (busy.value || !choices.value[item.app_binding_id]) return
  const token = generation
  busy.value = true; error.value = ''; message.value = ''
  try {
    await cloudApi.put(`${path()}/app-bindings/${encodeURIComponent(item.app_binding_id)}/persona`, {
      persona_id: choices.value[item.app_binding_id], expected_revision: item.binding_revision,
    })
    if (token !== generation) return
    message.value = '当前人设已确认，原项目绑定和历史任务已保留。'
    await load(); emit('changed')
  } catch (cause) { if (token === generation) error.value = cause.message || '本次确认未完成，请重新读取。' }
  finally { if (token === generation) busy.value = false }
}
watch(() => [props.tenantId, props.projectId], () => {
  generation++; busy.value = false; error.value = ''; data.value = null; choices.value = {}; message.value = ''
  if (props.tenantId && props.projectId) void load()
}, { immediate: true })
onBeforeUnmount(() => { generation++ })
</script>

<template>
  <section class="binding-readiness" aria-label="自动任务接收范围">
    <header><h2>自动任务接收范围</h2><button type="button" :disabled="busy" @click="load">重新读取接收名单</button></header>
    <p v-if="data">正式用户 {{ data.production_count }} 位 · 当前可执行 {{ data.eligible_production_count }} 位 · 待确认人设 {{ data.needs_reassignment_count }} 位</p>
    <p v-else>{{ busy ? '正在核对项目绑定与当前人设…' : '接收人数尚未确认。' }}</p>
    <p v-if="data?.test_binding_count">旧测试绑定不计入正式用户人数。</p>
    <p v-if="error" role="alert">{{ error }}</p><p v-if="message" role="status">{{ message }}</p>
    <div v-for="item in pending" :key="item.app_binding_id" class="binding-row">
      <strong>{{ item.display_name || `用户 ${item.app_binding_id.slice(-6)}` }} {{ item.masked_phone || '' }}</strong>
      <p>原人设：{{ item.persona_name }}。请确认后续任务使用的当前人设。</p>
      <label>当前人设<select v-model="choices[item.app_binding_id]" :aria-label="`为${item.display_name || '这位用户'}选择当前人设`" :disabled="busy">
        <option :value="undefined">请选择当前人设</option><option v-for="persona in data.current_personas" :key="persona.id" :value="persona.id">{{ persona.name }}</option>
      </select></label>
      <button type="button" :disabled="busy || !choices[item.app_binding_id]" @click="confirm(item)">确认当前人设</button>
    </div>
  </section>
</template>

<style scoped>
.binding-readiness { display: grid; gap: 12px; padding: 22px; border: 1px solid #c7dfdc; border-radius: 16px; background: #fff; min-width: 0; }
.binding-readiness header { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }.binding-readiness h2,.binding-readiness p { margin: 0; }
.binding-readiness button { min-height: 42px; padding: 9px 14px; border: 1px solid #9fc8c2; border-radius: 9px; color: #08746d; background: #edf8f5; }
.binding-row { display: grid; gap: 10px; padding: 14px; border: 1px solid #d8e3e7; border-radius: 10px; min-width: 0; overflow-wrap: anywhere; }.binding-row label { display: grid; gap: 6px; }
.binding-row select { width: 100%; min-width: 0; min-height: 44px; font: inherit; }.binding-readiness [role=alert] { color: #a9342e; }
</style>
